/**
 * Type-safe SessionStorage service with error handling
 */
import {isClient} from "@/shared/utils/runtime";

export interface SessionStorageOptions {
    /** Prefix for all keys */
    prefix?: string;
    storage?: "session" | "local";
}

class SessionStorageService {
    private readonly prefix: string;
    private readonly storageType: "session" | "local";

    constructor(options: SessionStorageOptions = {}) {
        this.prefix = options.prefix || "";
        this.storageType = options.storage || "session";
    }

    private get storage(): Storage {
        return this.storageType === "local" ? localStorage : sessionStorage;
    }

    private getKey(key: string): string {
        return this.prefix ? `${this.prefix}:${key}` : key;
    }

    /**
     * Get item from sessionStorage
     */
    get<T>(key: string): T | null {
        if (!isClient()) return null;

        try {
            const item = this.storage.getItem(this.getKey(key));
            return item ? JSON.parse(item) : null;
        } catch (e) {
            console.warn(`[SessionStorage] Failed to get "${key}":`, e);
            return null;
        }
    }

    /**
     * Set item in sessionStorage
     */
    set<T>(key: string, value: T): boolean {
        if (!isClient()) return false;

        try {
            this.storage.setItem(this.getKey(key), JSON.stringify(value));
            return true;
        } catch (e) {
            console.warn(`[SessionStorage] Failed to set "${key}":`, e);
            return false;
        }
    }

    /**
     * Remove item from sessionStorage
     */
    remove(key: string): boolean {
        if (!isClient()) return false;

        try {
            this.storage.removeItem(this.getKey(key));
            return true;
        } catch (e) {
            console.warn(`[SessionStorage] Failed to remove "${key}":`, e);
            return false;
        }
    }

    /**
     * Clear all items with this service's prefix
     */
    clear(): void {
        if (!isClient()) return;

        if (!this.prefix) {
            this.storage.clear();
            return;
        }

        // Remove only items with our prefix
        const keysToRemove: Array<string> = [];
        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);
            if (key?.startsWith(this.prefix)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => this.storage.removeItem(key));
    }

    /**
     * Check if key exists
     */
    has(key: string): boolean {
        if (!isClient()) return false;
        return this.storage.getItem(this.getKey(key)) !== null;
    }
}

export type SessionStorageInstance = SessionStorageService;

export function SessionStorage(options?: SessionStorageOptions): SessionStorageInstance {
    return new SessionStorageService(options);
}

// Pre-configured instance for search results cache
export function SearchResultsCache(): SessionStorageInstance {
    return SessionStorage({prefix: "search_results_cache"});
}

export function TripSessionCache(): SessionStorageInstance {
    return SessionStorage({prefix: "trip_session", storage: "local"});
}
